import { useState, useEffect, useCallback } from 'react';
import { fetchData } from '../api';

export interface PhaseData {
  phase1: number;
  phase2: number;
  phase3: number;
  total?: number;
}

export interface SystemData {
  solarPower: number;
  loadPower: PhaseData & { total: number };
  meterPower: PhaseData & { total: number };
  meterVolts: PhaseData & { frequency?: number };
  meterAmps: PhaseData;
  meterReactivePower: PhaseData;
  deratePower: number;
  increasePower: number;
  deratingEmergency: boolean;
  calculatedLineResistance: PhaseData;
  // Export control tracking
  curtailedPower?: number;      // Estimated power being curtailed (W)
  currentRateLimit?: number;    // Current power limit (0-10000 = 0-100%)
  systemCapacity?: number;      // Total rated PV capacity (W)
  lastControlCommandTime?: number; // Timestamp of last control command

  // Battery headroom tracking
  availableBatteryChargeHeadroom?: number;  // How much more power batteries can absorb (W)
  totalBatteryChargePower?: number;         // Current total battery charge power (negative = charging)
  totalBatteryMaxChargePower?: number;      // Total max charge power across all batteries
  batteryAbsorptionExpected?: number;       // Power we expect batteries to absorb (for failsafe)
}

export function useSystemData(pollingInterval = 1000) {
  const [data, setData] = useState<SystemData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchSystemData = useCallback(async () => {
    try {
      const result = await fetchData('/system');
      setData(result);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSystemData();
    const interval = setInterval(fetchSystemData, pollingInterval);
    return () => clearInterval(interval);
  }, [fetchSystemData, pollingInterval]);

  return { data, loading, error, lastUpdated, refetch: fetchSystemData };
}
